import React from 'react';
import { callApi } from 'src/utils/apiCall';
import { getCached, setCached, STATIC_LOOKUP_CACHE_TTL_MS } from 'src/utils/staticLookupCache';
import { getPracticeContextCacheKey } from 'src/redux/slices/mergeTags';
import {
    resolveMappedTemplate,
    buildQuickReplyContext,
    stripBlankContextValues,
    renderQuickReplyPreview,
    findUnresolvedTemplateVars,
    computeQuickReplyTimeOfDay,
} from 'src/components/whatsappComponents/quickReplyUtils';
import { getTemplateBody } from 'src/components/whatsappComponents/whatsAppTemplateUtils';

function getTreatmentName(leadDetails) {
    const treatmentRow = leadDetails?.lead_treatments?.[0] || null;
    const treatment = treatmentRow?.treatment;
    const topLevelTreatment = leadDetails?.treatment;
    return (
        treatmentRow?.treatment_name
        || (typeof treatment === 'string' ? treatment : null)
        || treatment?.name
        || treatment?.treatment_item?.name
        || treatmentRow?.treatment_item?.name
        || leadDetails?.treatment_name
        || (typeof topLevelTreatment === 'string' ? topLevelTreatment : null)
        || topLevelTreatment?.name
        || topLevelTreatment?.treatment_item?.name
        || ''
    );
}

// Per-mount memo of lead get_context() payloads, keyed by "practiceId:leadId".
// Deliberately module-level-free (created fresh per hook instance via useRef)
// so switching leads/practices can never leak a stale entry across a DIFFERENT
// hook instance, while still avoiding a re-fetch when the SAME lead is
// revisited within one chat session (e.g. dismiss → re-open the draft).
function useLeadContextMemo() {
    return React.useRef(new Map()).current;
}

const IDLE_CONTEXT_FETCH = Object.freeze({ status: 'ready', practiceContext: {}, leadContext: {} });

/**
 * Fetch the FULL backend `get_context()` payloads for a practice and (when
 * known) a lead, so the mapped-template preview/blocked-check/send-context
 * covers every field the backend can fill — not just the handful of short
 * aliases `buildQuickReplyContext` knows about. These are the SAME endpoints
 * `src/redux/slices/mergeTags.js` already uses for the merge-tag preview
 * system:
 *   - GET /api/v1/practices/{practiceId}/get_context/
 *   - GET /api/v1/practices/{practiceId}/leads/{leadId}/get_context/
 *     (LeadViewset.GetContextMixin — apps/leads/views.py)
 *
 * Exposes a tri-state `status` so the caller can distinguish "still loading"
 * (don't report anything as missing yet) from "failed" (a distinct error,
 * not a false "missing details") from "ready" (the real blocked/missing
 * check may now run).
 *
 * @param {string|number|null} practiceId
 * @param {string|number|null} leadId
 * @returns {{status: 'loading'|'ready'|'error', practiceContext: object, leadContext: object, retry: Function}}
 */
function useBackendTemplateContext(practiceId, leadId) {
    const leadContextMemo = useLeadContextMemo();
    const [state, setState] = React.useState(() => (
        practiceId ? { status: 'loading', practiceContext: {}, leadContext: {} } : IDLE_CONTEXT_FETCH
    ));
    // Bumped by retry() to force a re-fetch even when practiceId/leadId haven't
    // changed (e.g. retrying after a network error).
    const [retryToken, setRetryToken] = React.useState(0);

    React.useEffect(() => {
        let cancelled = false;
        if (!practiceId) {
            setState(IDLE_CONTEXT_FETCH);
            return undefined;
        }
        // Reset to loading immediately (not just on first mount) so a
        // practice/lead switch never shows the PREVIOUS practice/lead's
        // context while the new one is in flight.
        setState({ status: 'loading', practiceContext: {}, leadContext: {} });

        (async () => {
            try {
                const practiceCacheKey = getPracticeContextCacheKey(practiceId);
                let practiceContext = getCached(practiceCacheKey, { ttlMs: STATIC_LOOKUP_CACHE_TTL_MS });
                if (practiceContext === undefined) {
                    const res = await callApi(`/api/v1/practices/${practiceId}/get_context/`);
                    if (res?.status !== 200) throw new Error('practice get_context/ failed');
                    practiceContext = res?.data ?? {};
                    setCached(practiceCacheKey, practiceContext);
                }

                let leadContext = {};
                if (leadId) {
                    const leadCacheKey = `${practiceId}:${leadId}`;
                    if (leadContextMemo.has(leadCacheKey)) {
                        leadContext = leadContextMemo.get(leadCacheKey);
                    } else {
                        const res = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/get_context/`);
                        if (res?.status === 404) {
                            // ARCHIVED leads 404 here — LeadViewset.get_queryset
                            // scopes to Lead.objects (is_archived=False), so an
                            // archived lead has no get_context/ route at all. This
                            // is expected, not a failure: treat it as "no extra
                            // lead context available" and still reach 'ready' so
                            // the live leadDetails short-key aliases (first_name,
                            // etc.) keep working and a retry isn't required (a
                            // retry would just 404 again forever).
                            leadContext = {};
                        } else if (res?.status !== 200) {
                            throw new Error('lead get_context/ failed');
                        } else {
                            leadContext = res?.data ?? {};
                        }
                        leadContextMemo.set(leadCacheKey, leadContext);
                    }
                }

                if (!cancelled) setState({ status: 'ready', practiceContext, leadContext });
            } catch (e) {
                if (!cancelled) setState({ status: 'error', practiceContext: {}, leadContext: {} });
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadId, retryToken]);

    const retry = React.useCallback(() => setRetryToken((n) => n + 1), []);

    return { ...state, retry };
}

/**
 * Drives the mapped-template draft flow: a checklist WhatsApp item resolves a
 * per-practice mapped template ({ id, name }); we match it in the loaded template
 * list, render a fully-merged draft-preview, and expose a send-context so the
 * host (ChatPage) can send it via the approved-template path — which works
 * whether the 24-hour window is open OR closed.
 *
 * Matching is ID-FIRST with an exact-name fallback (see resolveMappedTemplate).
 *
 * Blocked state: if the template has required {{variables}} that can't be
 * resolved from lead/practice context (would send as blank "-"), `draft.blocked`
 * is true and `draft.missingFields` lists them — the host must surface a clear
 * warning and disable Send rather than silently sending placeholders.
 * `draft.status` distinguishes WHY: 'loading' (context still being fetched —
 * never report fields as missing yet), 'error' (the fetch failed — a distinct
 * message + retry, not a false "missing details"), or 'ready' (the check
 * reflects real, current data).
 *
 * IMPORTANT — `draft.sendContext` vs the preview/blocked-check: the fetched
 * practice/lead get_context() SNAPSHOT (see useBackendTemplateContext) is used
 * ONLY to widen the PREVIEW and the missing-field CHECK to every backend
 * field. It is deliberately NEVER merged into `sendContext` — the snapshot can
 * be stale (practice: up to a 10-minute cache; lead: cached for the life of
 * this hook instance) and `sendWhatsappMessage` already fetches a FRESH
 * get_context() at click time, applying the hook's extraContext (i.e.
 * `sendContext`) LAST on top of it. A stale value in `sendContext` would win
 * over both that fresh fetch and live `leadDetails` — backwards. So
 * `sendContext` stays exactly the short-alias/live-hint object this hook has
 * always sent. `sendContext` is ALSO run through `stripBlankContextValues`
 * before being returned: a hint the caller genuinely doesn't have yet (e.g.
 * `practiceName` before Redux populates it) renders as `''`, and since
 * `buildWhatsappSendContext` spreads extraContext unconditionally LAST, a
 * blank string there would clobber the correct value the backend's own fresh
 * fetch just resolved. Leaving the key out entirely lets the fresh fetch fill
 * it instead of overwriting it with nothing.
 *
 * @param {object}              opts
 * @param {Array}               opts.templates    - loaded WhatsApp template list (WABA-scoped)
 * @param {string|number|null}  opts.templateId   - resolved WhatsappMessageTemplate PK
 * @param {string|null}         opts.templateName - resolved Meta template name (fallback)
 * @param {string|number|null}  opts.itemId       - the checklist/task item id the mapping
 *                                                  came from; scopes the dismissed flag so
 *                                                  two items sharing a template don't
 *                                                  suppress each other's draft
 * @param {object}              opts.leadDetails  - current lead detail object
 * @param {string}              opts.practiceName - practice display name (live hint; used
 *                                                  for BOTH sendContext and, when non-blank,
 *                                                  takes priority over the fetched snapshot
 *                                                  in the preview/check too)
 * @param {string}              [opts.practicePhone] - practice contact phone (live hint;
 *                                                  same priority rule as practiceName)
 * @param {string|number|null}  [opts.practiceId] - the practice to fetch FULL backend
 *   context for (apps.practice.models.Practice.get_context()). When omitted, no
 *   fetch happens and the hook behaves exactly as before (short-alias-only,
 *   always 'ready') — used by callers/tests that already resolve everything
 *   synchronously.
 * @param {string|number|null}  [opts.leadId] - the lead to fetch FULL backend context
 *   for (apps.leads.models.Lead.get_context(), via LeadViewset's GetContextMixin).
 *   Defaults to `leadDetails?.id` when omitted.
 * @returns {{draft: (object|null), dismiss: Function, markSent: Function, retryContext: Function}}
 */
export default function useMappedTemplateDraft({
    templates,
    templateId,
    templateName,
    itemId,
    leadDetails,
    practiceName,
    practicePhone,
    practiceId,
    leadId,
}) {
    // Stable identity for the current mapping — used to reset the dismissed flag
    // when the checklist item changes to a different template.
    const mappedKey = templateId ?? templateName ?? null;
    // Dismissal is scoped to the SPECIFIC item (template + item id): two distinct
    // checklist items that happen to share the same mapped template must not
    // suppress each other's draft.
    const dismissScopeKey = mappedKey != null ? `${mappedKey}::${itemId ?? ''}` : null;
    const [dismissedKey, setDismissedKey] = React.useState(null);

    const effectiveLeadId = leadId ?? leadDetails?.id ?? null;
    const backendContext = useBackendTemplateContext(practiceId ?? null, effectiveLeadId);

    const draft = React.useMemo(() => {
        if (!mappedKey) return null;
        if (dismissedKey === dismissScopeKey) return null;
        const matched = resolveMappedTemplate(templates, templateId, templateName);
        // Templates not loaded yet, or no such template in this WABA → no draft.
        if (!matched) return null;

        const firstNameHint = leadDetails?.first_name || '';
        const lastNameHint = leadDetails?.last_name || '';
        // Only the treatment name — never the raw treatment object. Lead payloads
        // vary by entry point (table rows, FollowUpV2, WA metadata), so accept
        // the same common shapes the rest of WhatsApp already displays.
        const treatmentHint = getTreatmentName(leadDetails);
        const practiceNameHint = practiceName || '';
        const practicePhoneHint = practicePhone || '';

        const bodyMapping = matched?.template_context_mapping?.BODY;
        const body = getTemplateBody(matched) || '';

        // sendContextRaw = EXACTLY what this hook has always sent: the FE's
        // short aliases (+ backend-name aliases for first/last name and
        // phone) built from LIVE hints, nothing else. It deliberately does
        // NOT include the fetched practice/lead get_context() snapshot below
        // — that snapshot can be stale (practice: up to a 10-minute
        // sessionStorage TTL; lead: cached for the life of this hook
        // instance, no TTL at all), and `sendWhatsappMessage`
        // (src/redux/slices/whatsappV2.js) already fetches a FRESH
        // get_context() at click time and applies this extraContext LAST on
        // top of it (buildWhatsappSendContext). If the snapshot were included
        // here, a stale cached value would win over both the fresh backend
        // fetch AND live leadDetails at send time — exactly backwards. The
        // snapshot is for the PREVIEW and the blocked-check only, never for
        // what's actually sent.
        const sendContextRaw = buildQuickReplyContext({
            firstNameHint,
            lastNameHint,
            treatmentHint,
            practiceNameHint,
            practicePhoneHint,
        });
        // A hint this hook hasn't actually got yet (e.g. practiceName before
        // Redux populates it, or no practicePhone hint passed in at all)
        // renders as '' from buildQuickReplyContext. `buildWhatsappSendContext`
        // spreads extraContext LAST and UNCONDITIONALLY, so an in-band ''
        // would clobber the correct value its own fresh get_context() fetch
        // just resolved — the agent's preview (below) shows the right value,
        // but the patient would receive a message with that field blanked
        // out. Stripping blanks here means a key we don't genuinely know is
        // simply absent from extraContext, leaving the backend's fresh fetch
        // to fill it — never overwritten with nothing. `answer` is exempt
        // (see CONTEXT_KEYS_NEVER_STRIPPED): an intentionally empty typed
        // message must stay empty, not silently fall back to something else.
        const sendContext = stripBlankContextValues(sendContextRaw);

        // Preview/check context: start from the FULL backend get_context()
        // snapshot (practice_email, practice_whatsapp, address fields, the
        // real time_of_day, lead_contact/lead_address/lead_source keys, etc. —
        // everything the short aliases above don't know about), then let any
        // NON-BLANK live short-key value win over it. Live data is always the
        // more current source when both exist; the snapshot only fills in
        // what live data doesn't have an alias for.
        const backendSnapshot = { ...backendContext.practiceContext, ...backendContext.leadContext };
        const previewContext = { ...backendSnapshot };
        Object.entries(sendContextRaw).forEach(([key, value]) => {
            if (value !== null && value !== undefined && String(value).trim() !== '') {
                previewContext[key] = value;
            }
        });
        // time_of_day is preview-only (never sent — see buildQuickReplyContext)
        // and isn't one of the live short keys above, so it doesn't win by the
        // rule above. Prefer the backend's REAL Europe/London value when the
        // snapshot has it; only fall back to the local clock guess otherwise.
        if (!previewContext.time_of_day) {
            previewContext.time_of_day = computeQuickReplyTimeOfDay();
        }

        const rendered = renderQuickReplyPreview(body, previewContext, bodyMapping);

        // The blocked/missing check must NEVER run against a partial context —
        // that would flag every field the fetch hasn't filled in yet as
        // "missing". Only compute it once the full context is 'ready'; while
        // loading or on error, missingFields stays [] and the caller instead
        // renders a neutral "checking" / distinct error state (driven by
        // `status`), not a false "missing details" warning. Runs against
        // previewContext (live-priority merge), NOT sendContext — the send
        // path resolves its own fresh context independently.
        const missingFields = backendContext.status === 'ready'
            ? findUnresolvedTemplateVars(body, previewContext, bodyMapping)
            : [];

        return {
            name: matched.name,
            // QuickReplyDraftBubble with hasAnswerSlot=false renders `before` only.
            preview: { before: rendered, answer: '', after: '', hasAnswer: false, hasAnswerSlot: false },
            sendContext,
            missingFields,
            status: backendContext.status,
            // Disabled whenever we can't yet vouch for the data: still loading,
            // the fetch failed, OR (once ready) a field is genuinely empty.
            blocked: backendContext.status !== 'ready' || missingFields.length > 0,
        };
    }, [
        mappedKey,
        dismissScopeKey,
        dismissedKey,
        templates,
        templateId,
        templateName,
        leadDetails,
        practiceName,
        practicePhone,
        backendContext.status,
        backendContext.practiceContext,
        backendContext.leadContext,
    ]);

    // Dismiss the draft (fall back to the normal composer / Personal Reply) — for
    // THIS item only.
    const dismiss = React.useCallback(() => {
        if (dismissScopeKey) setDismissedKey(dismissScopeKey);
    }, [dismissScopeKey]);

    // Called after a successful send so the draft doesn't re-open for THIS item.
    const markSent = React.useCallback(() => {
        if (dismissScopeKey) setDismissedKey(dismissScopeKey);
    }, [dismissScopeKey]);

    return { draft, dismiss, markSent, retryContext: backendContext.retry };
}
